import { DeleteForeverRounded, ViewAgenda, ViewAgendaOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import React, { ChangeEvent, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import DialogHeader from "./dialogs/DialogHeader";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { getTranslation } from "common";
import { useForm, useTranslations, useUser, useSelectedOrganization } from "hooks";
import { Validation, isRequired } from "hooks/useForm";
import FieldsListItem from "./FieldsListItem";
import { UserData, UserDataUpdate } from "hooks/useUserDataKeys";
import { Data } from "./dialogs/ProcessModelSchema";
import { create_UUID, getUserSchema } from "common/utilities";
import {
  rowsToFields,
  type LegacySchemaRow,
} from "components/custom-fields/legacyAdapter";
import { useCustomFields } from "hooks";
import type { AttachmentHost } from "components/custom-fields/attachmentField";
import Userfield from "./Userfield";
import CustomFieldInput from "components/custom-fields/CustomFieldInput";
import { finishUploadsAndSave } from "components/custom-fields/runtimeDraft";
import type { FieldDefinition, FieldType, FieldValue, RuntimeContext } from "model/CustomFields";
import { FIELD_GROUPS, groupLabelKey, isServerAssigned, newField, typeLabelKey } from "components/custom-fields/fieldTypes";
import TypeIcon from "components/custom-fields/typeIcon";
import FieldConfigurationDialog from "components/custom-fields/FieldConfigurationDialog";
import { controlsFor } from "components/custom-fields/controlKinds";
import { choiceConfigIssues } from "components/custom-fields/fieldTypes";
import { validateFieldConfig } from "components/custom-fields/fieldConfig";
import { definitionForUserdata, toUserdata } from "components/custom-fields/userdata";
import { loadCardPreferences, saveCardPreference } from "components/custom-fields/incardPreference";
import useCardPreferences from "hooks/useCardPreferences";

interface FieldsListProps {
  canManage?: boolean;
  readonly?: boolean;
  onChange: (newValues: { [key: string]: UserData }) => void;
  /** Refresh the owner's task projection after a save or a card preference change. */
  onRefresh?: () => void;
  value: { [key: string]: UserData };
  schema: string | undefined;
  selectedProcess: number | undefined;
  handleSaveUserdata?: (
    newUserdata: { [key: string]: UserDataUpdate },
    silentUpdate?: boolean,
    operationId?: string,
    removedFieldIds?: readonly string[],
    context?: RuntimeContext
  ) => Promise<void>;
  linkChange?: boolean;
  enableUndefined?: boolean;
  openAddFieldDialog?: boolean;
  onAddFieldDialogClose?: () => void;
  /**
   * Il processo, quando esiste.
   *
   * Con questo la lista lavora sul **contesto autorizzato**: il server dice
   * quali campi esistono, quali si possono scrivere e con quale valore. Senza,
   * resta il percorso legacy sulla mappa del genitore - ed e' il punto di
   * transizione, perche' una migrazione in blocco delle cinque superfici che
   * usano questa lista le romperebbe tutte insieme.
   */
  processId?: string;
  taskId?: string;
  /**
   * Come caricare un allegato, dove si può.
   *
   * Il caricamento passa dal token dell'istanza del flownodo, che appartiene
   * alla superficie — non al renderer: la lista lo riceve e lo passa al campo,
   * e dove non c'è il campo allegato resta in sola lettura. Additivo come
   * `processId`: le superfici si cablano una alla volta.
   */
  attachments?: AttachmentHost | null;
  definitions?: readonly FieldDefinition[];
  modelId?: string;
  pendingFiles?: Record<string, File[]>;
  onStageFiles?: (fieldId: string, files: FileList | null) => void;
  onDiscardFiles?: (fieldId: string) => void;
  onRemoveFile?: (fieldId: string, fileName: string) => void;
}

export interface FieldsListHandle {
  flush: () => Promise<boolean>;
}

/**
 * L'ingombro di una riga di campo, in px.
 *
 * E' l'altezza del `TextField size="small"` che `CustomFieldInput` rende per
 * quasi tutti i tipi — il controllo piu' alto della riga, perche' gl'IconButton
 * che gli stanno accanto sono alti 32. E' l'unica misura del file, e sta qui
 * perche' lo skeleton deve occupare esattamente la riga che sostituisce:
 * sbagliarla sposterebbe il contenuto quanto non riservarlo affatto.
 */
const FIELD_ROW_HEIGHT = 40;

/** Righe da disegnare quando lo userschema non dice quanti campi attendersi. */
const FALLBACK_SKELETON_ROWS = 3;

/**
 * Da questa soglia in su i campi si dispongono a coppie, due per riga.
 * Sotto, due colonne stringono campi che stanno benissimo uno per riga:
 * la lista e' abbastanza lunga da valere la pena di dimezzarne l'altezza
 * solo da 5 campi in poi.
 */
const PAIRED_LAYOUT_THRESHOLD = 5;

const FieldsList = React.forwardRef<FieldsListHandle, FieldsListProps>(({
  readonly,
  canManage = true,
  schema,
  selectedProcess,
  onChange,
  onRefresh,
  value,
  handleSaveUserdata: _handleSaveUserData,
  linkChange = false,
  enableUndefined = false,
  openAddFieldDialog,
  onAddFieldDialogClose,
  processId,
  taskId,
  attachments = null,
  definitions,
  modelId,
  pendingFiles,
  onStageFiles,
  onDiscardFiles,
  onRemoveFile,
}, ref) => {
  /**
   * Il runtime dei campi, quando c'e' un processo.
   *
   * Senza `processId` l'hook e' **inerte**: non carica niente e non ha bozza.
   * E' cio' che rende questo cablaggio additivo invece che una rottura.
   */
  const runtime = useCustomFields({ processId, taskId, onSaved: onRefresh });
  const saveTimer = useRef<number | null>(null);
  const pendingUploads = useRef(new Set<Promise<readonly (string | number)[]>>());
  const contextMatches = runtime.context?.processId === processId &&
    (!taskId || runtime.context?.taskId === taskId);
  const matchedContext = contextMatches ? runtime.context : null;
  const fieldAttachmentIds = useMemo(() =>
    (matchedContext?.fields ?? [])
      .filter((field) => field.definition.type === "attachment")
      .flatMap((field) => {
        const current = runtime.draft?.values;
        const fieldValue = current && Object.prototype.hasOwnProperty.call(current, field.fieldId)
          ? current[field.fieldId] : field.value;
        return Array.isArray(fieldValue) ? fieldValue.map(String)
          : typeof fieldValue === "string" && fieldValue ? [fieldValue] : [];
      }),
    [matchedContext, runtime.draft]
  );
  const onFieldAttachmentIdsChange = attachments?.onFieldAttachmentIdsChange;
  useEffect(() => {
    onFieldAttachmentIdsChange?.(fieldAttachmentIds);
  }, [onFieldAttachmentIdsChange, fieldAttachmentIds]);
  const trackedAttachments: AttachmentHost | null = attachments && {
    ...attachments,
    upload: (files) => {
      const upload = attachments.upload(files);
      pendingUploads.current.add(upload);
      void upload.then(
        () => pendingUploads.current.delete(upload),
        () => pendingUploads.current.delete(upload)
      );
      return upload;
    },
  };

  useImperativeHandle(ref, () => ({
    flush: async () => {
      if (!processId) return true;
      if (!contextMatches) return false;
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      return finishUploadsAndSave(Array.from(pendingUploads.current), runtime.save);
    },
  }));

  // Autosave after the draft has actually changed. Calling save in the same
  // event as setValue reads the previous React state and sends an empty patch.
  useEffect(() => {
    if (!contextMatches || !runtime.hasChanges || runtime.loading || runtime.error) return;
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      void runtime.save();
    }, 500);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    };
  }, [contextMatches, runtime.draft, runtime.context, runtime.hasChanges, runtime.loading, runtime.error]);
  /** I valori legacy sono letti solo quando non c'e' un contesto canonico. */
  const valori = value;
  const [loading, setLoading] = useState(false);
  const pendingSave = useRef<{ payload: string; operationId: string } | null>(null);
  const [saveError, setSaveError] = useState(false);
  const runtimeValueFor = (fieldId: string, fallback: FieldValue): FieldValue => {
    if (runtime.draft?.values && Object.prototype.hasOwnProperty.call(runtime.draft.values, fieldId)) {
      return runtime.draft.values[fieldId];
    }
    return fallback;
  };
  const normalizeRuntimeUserdata = (
    userdata: { [key: string]: UserData }
  ): { [key: string]: UserData } => {
    if (!contextMatches || !runtime.context) return userdata;
    const next = { ...userdata };
    for (const field of runtime.context.fields) {
      if (field.adHoc !== true) continue;
      const key = field.definition.legacyKey ?? field.definition.label;
      const entry = Object.entries(next).find(([entryKey, data]) =>
        data.fieldId === field.fieldId || entryKey === key
      );
      if (!entry) continue;
      const [entryKey, data] = entry;
      next[entryKey] = userdataForRuntimeField(field, data, data.inCard === true);
    }
    return next;
  };
  const runtimeAdHocFieldFor = (key: string, data: UserData) =>
    contextMatches && runtime.context
      ? runtime.context.fields.find((field) =>
        field.adHoc === true &&
        (data.fieldId === field.fieldId || key === (field.definition.legacyKey ?? field.definition.label))
      )
      : undefined;
  const metadataForSave = (key: string, data: UserData): UserDataUpdate => {
    const field = runtimeAdHocFieldFor(key, data);
    if (!field || !(key in value)) return data;
    const { value: _value, ...metadata } = userdataForRuntimeField(field, data, data.inCard === true);
    return metadata;
  };
  const sameMetadata = (left: UserDataUpdate | undefined, right: UserDataUpdate): boolean => {
    if (!left) return false;
    const { value: _leftValue, ...leftMetadata } = left;
    const { value: _rightValue, ...rightMetadata } = right;
    return JSON.stringify(leftMetadata) === JSON.stringify(rightMetadata);
  };
  const userdataDeltaForSave = (
    next: { [key: string]: UserData }
  ): { [key: string]: UserDataUpdate } => {
    const delta: { [key: string]: UserDataUpdate } = {};
    for (const [key, data] of Object.entries(next)) {
      const runtimeField = runtimeAdHocFieldFor(key, data);
      const update = metadataForSave(key, data);
      const previous = runtimeField
        ? metadataForSave(key, value[key])
        : value[key];
      if (runtimeField
        ? sameMetadata(previous, update)
        : JSON.stringify(previous) === JSON.stringify(update)) continue;
      delta[key] = update;
    }
    return delta;
  };

  /** Applica una modifica dei soli metadati legacy senza schema. */
  const applica = async (
    mappaIntera: { [key: string]: UserData },
    silentUpdate?: boolean,
    removedFieldIds: readonly string[] = []
  ) => {
    const normalized = normalizeRuntimeUserdata(mappaIntera);
    if (!await handleSaveUserdata(normalized, silentUpdate, removedFieldIds)) return false;
    onChange(normalized);
    return true;
  };
  const handleSaveUserdata = async (
    newUserdata: { [key: string]: UserData },
    silentUpdate?: boolean,
    removedFieldIds: readonly string[] = []
  ) => {
    if (_handleSaveUserData) {
      setLoading(true);
      setSaveError(false);
      if (processId && !contextMatches) {
        setLoading(false);
        setSaveError(true);
        return false;
      }
      if (processId && contextMatches) {
        if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
        if (!await finishUploadsAndSave(Array.from(pendingUploads.current), runtime.save)) {
          setLoading(false);
          setSaveError(true);
          return false;
        }
      }
      const userdataUpdate = contextMatches && runtime.context
        ? userdataDeltaForSave(newUserdata)
        : newUserdata;
      // A flush may have advanced the revision before React renders again.
      const saveContext = processId ? runtime.getCurrentContext() : undefined;
      if (processId && (!saveContext || saveContext.processId !== processId ||
          (taskId && saveContext.taskId !== taskId))) {
        setLoading(false);
        setSaveError(true);
        return false;
      }
      const payload = JSON.stringify({ userdataUpdate, removedFieldIds,
        contextId: saveContext?.contextId, revision: saveContext?.revision });
      if (pendingSave.current?.payload !== payload) pendingSave.current = { payload, operationId: create_UUID() };
      try {
        await _handleSaveUserData(userdataUpdate, silentUpdate, pendingSave.current.operationId, removedFieldIds, saveContext ?? undefined);
        pendingSave.current = null;
        if (processId) await runtime.reload();
      } catch {
        setSaveError(true);
        return false;
      } finally { setLoading(false); }
    }
    return true;
  };
  const [check, setCheck] = useState<string[]>([]);
  const user = useUser();
  const organization = useSelectedOrganization();
  // La preferenza "in card" e' del **modello**, non del processo: tutti i
  // processi di un modello la ereditano. Il processo rende il contesto, il
  // modello dice cosa si vede in card.
  const cardModelId = modelId ?? matchedContext?.modelId;
  const cardPreferences = useCardPreferences(cardModelId);
  const [savingPreference, setSavingPreference] = useState(false);
  const preferenceScope = `${user?.userId}:${organization?.id}:${cardModelId}`;
  const activePreferenceScope = useRef(preferenceScope);
  activePreferenceScope.current = preferenceScope;
  useEffect(() => {
    setSavingPreference(false);
    // Ripristino dalla memoria locale: nessuna rete, nessun errore da riportare.
    if (user?.userId && organization?.id && cardModelId) {
      void loadCardPreferences(user.userId, organization.id, [Number(cardModelId)]);
    }
  }, [user?.userId, organization?.id, cardModelId]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<string | null>(null);
  const [newDefinition, setNewDefinition] = useState<FieldDefinition>(() => newField("text", 0));
  const [configuring, setConfiguring] = useState(false);
  const translations = useTranslations();
  const userSchema: Data[] = useMemo(() => getUserSchema(schema), [schema]);

  /**
   * Le stesse definizioni, nella forma del contratto.
   *
   * Serve al renderer dei 16 tipi: quello legacy conosce quattro tipi e
   * indovina il resto. La conversione avviene **una volta** e non a ogni
   * campo, e non cambia cosa si salva: la persistenza resta legacy finche'
   * il bridge non la deriva dal server.
   */
  const fieldDefinitions = useMemo(
    () => matchedContext?.fields.map((field) => field.definition) ??
      (definitions ?? (processId ? [] : rowsToFields(userSchema as unknown as LegacySchemaRow[]))),
    [userSchema, definitions, matchedContext, processId]
  );
  const definitionByKey = useMemo(
    () =>
      new Map(
        fieldDefinitions.map((definition) => [
          definition.legacyKey ?? definition.label,
          definition,
        ])
      ),
    [fieldDefinitions]
  );
  const declaredKeys = useMemo(
    () => new Set<string>(fieldDefinitions.flatMap((definition) => [
      definition.fieldId,
      definition.legacyKey ?? definition.label,
    ]).filter((key): key is string => typeof key === "string" && key !== "")),
    [fieldDefinitions]
  );
  const extraDefinitions = useMemo(
    () => Object.entries(value).flatMap(([key, data]) => {
      const definition = definitionForUserdata(key, data);
      if (!definition || definition.deleted || declaredKeys.has(definition.fieldId)) return [];
      return [definition];
    }),
    [value, declaredKeys]
  );
  const formulaDefinitions = useMemo(() => {
    const byId = new Map<string, FieldDefinition>();
    for (const definition of [...fieldDefinitions, ...extraDefinitions]) {
      if (!definition.deleted) byId.set(definition.fieldId, definition);
    }
    return Array.from(byId.values());
  }, [fieldDefinitions, extraDefinitions]);
  const entryForRuntimeField = (field: NonNullable<typeof runtime.context>["fields"][number]) => {
    const key = field.definition.legacyKey ?? field.definition.label;
    return Object.entries(value).find(([entryKey, data]) =>
      data.fieldId === field.fieldId || entryKey === key
    );
  };
  const userdataForRuntimeField = (
    field: NonNullable<typeof runtime.context>["fields"][number],
    data: UserData,
    inCard: boolean
  ): UserData => ({
    ...data,
    fieldId: data.fieldId ?? field.fieldId,
    type: typeof data.type === "string" ? data.type : field.definition.type,
    config: data.config ?? field.definition.config,
    value: isServerAssigned(field.definition.type)
      ? data.value ?? null
      : runtimeValueFor(field.fieldId, field.value),
    inCard,
  });
  const validations: Validation[] = [
    ({ key }) =>
      isRequired(key) || {
        key: getTranslation(translations, "register.fieldrequired"),
      },
    ({ value, type }) =>
      type === "boolean" || type === "attachment" || isServerAssigned(type) ||
      (isRequired(value) || {
        value: getTranslation(translations, "register.fieldrequired"),
      }),
    ({ key }) =>
      Boolean(edit) ||
      (!(key in value) && !declaredKeys.has(key)) || {
        key: getTranslation(
          translations,
          "generic.keypresent.metadata.caption"
        ),
      },
  ];
  const { values, changeHandler, errors, touched, isValid, reset } = useForm(
    {
      key: "",
      value: null as FieldValue,
      type: "text" as FieldType,
    },
    validations
  );
  const draftDefinition = useMemo(
    () => ({ ...newDefinition, label: values.key }),
    [newDefinition, values.key]
  );
  const syncStoredCardKeys = (key: string, newCheck: boolean) => {
    setCheck((prev) => {
      const cp = newCheck
        ? Array.from(new Set([...prev, key]))
        : prev.filter((k) => k !== key);
      if (cp.length > 0) {
        localStorage.setItem(`userdata-per-${selectedProcess}`, cp.join(";"));
      } else {
        localStorage.removeItem(`userdata-per-${selectedProcess}`);
      }
      return cp;
    });
  };

  const onChangeInCard = async (_: ChangeEvent, newCheck: boolean, key: string) => {
    const newUserdata = {
      ...value,
      [key]: { ...value[key], inCard: newCheck },
    };
    if (await applica(newUserdata)) syncStoredCardKeys(key, newCheck);
  };

  const onChangeInCardSchema = async (newCheck: boolean, name: string) => {
    if (await applica({
      ...value,
      [name]: { ...value[name], inCard: newCheck },
    })) syncStoredCardKeys(name, newCheck);
  };

  const onChangeInCardPreference = async (fieldId: string, newCheck: boolean, modelDefault: boolean) => {
    if (!user?.userId || !organization?.id || !cardModelId || savingPreference) return;
    setSavingPreference(true);
    setSaveError(false);
    try {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      // Refresh only after pending drafts and uploads have reached the server.
      if (!await finishUploadsAndSave(Array.from(pendingUploads.current), runtime.save)) return;
      if (activePreferenceScope.current !== preferenceScope) return;
      // `modelDefault` distingue "scelta" da "default": uguale al default, la
      // voce si toglie invece di scriversi.
      saveCardPreference(user.userId, organization.id, cardModelId, fieldId, newCheck, modelDefault);
    } catch {
      if (activePreferenceScope.current === preferenceScope) setSaveError(true);
    } finally {
      if (activePreferenceScope.current === preferenceScope) setSavingPreference(false);
    }
  };
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    let newUserdata;
    const { key, ..._values } = values;
    const data = toUserdata({ ...newDefinition, label: key }, _values.value, check.includes(key));
    if (edit) {
      const { [edit]: _, ...newValue } = { ...value };
      newUserdata = { ...newValue, [key]: data };
    } else {
      newUserdata = { ...value, [key]: data };
    }
    if (await applica(newUserdata)) handleClose(false);
  };

  const onDelete = async (key: string, fieldId?: string) => {
    const { [key]: _, ...newValue } = { ...value };
    if (await applica(newValue, false, fieldId ? [fieldId] : [])) {
      if (fieldId) onDiscardFiles?.(fieldId);
    }
  };

  useEffect(() => {
    const actualStorage = localStorage.getItem(
      `userdata-per-${selectedProcess}`
    );
    if (actualStorage) {
      setCheck(actualStorage.split(";"));
    }
    if (!open) {
      reset();
      setEdit(null);
      setNewDefinition(newField("text", 0));
      setConfiguring(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  useEffect(() => {
    if (openAddFieldDialog) {
      setOpen(true);
    }
  }, [openAddFieldDialog]);

  const handleClose = (discardFiles = true) => {
    if (discardFiles) onDiscardFiles?.(newDefinition.fieldId);
    setOpen(false);
    if (onAddFieldDialogClose) {
      onAddFieldDialogClose();
    }
  };

  const onChangeSchemaValues = async (field: string, _value: unknown) => {
    const fieldSchema = userSchema.find((a) => a.name === field);
    let userdata: UserData;
    if (fieldSchema) {
      userdata = {
        inCard:
          field in value ? Boolean(valori[field]?.inCard) : fieldSchema.inCard,
        type: fieldSchema.type,
        value: String(_value),
      };
    } else {
      userdata = {
        ...valori[field],
        inCard: field in value ? Boolean(valori[field]?.inCard) : false,
        type: valori[field]?.type,
        value: typeof valori[field]?.type === "string" ? _value as FieldValue : String(_value),
      };
    }
    const newUserData = { ...value, [field]: userdata };

    if (JSON.stringify(valori[field]?.value) !== JSON.stringify(userdata.value)) {
      if (_handleSaveUserData) {
        await applica(newUserData, true);
        return;
      }
    }
    onChange(newUserData);
  };

  const configProblems = [
    ...validateFieldConfig(newDefinition.type, newDefinition.config),
    ...(["select", "multiselect", "radio"].includes(newDefinition.type) ? choiceConfigIssues(newDefinition.config) : []),
  ];

  // Campi effettivamente resi: e' il loro numero a decidere la disposizione,
  // non lo userschema, che contiene anche cio' che chi guarda non puo' vedere.
  // Gli allegati chiudono l'elenco: la loro riga è più alta e il nome del file
  // va letto per intero, quindi stanno sotto i campi normali (l'ordinamento è
  // stabile: fra gli allegati resta l'ordine del contesto).
  const renderedFields = useMemo(() => {
    const fields = runtime.context?.fields ?? [];
    return [...fields].sort(
      (a, b) =>
        Number(a.definition.type === "attachment") -
        Number(b.definition.type === "attachment")
    );
  }, [runtime.context]);
  const fieldsInPairs = renderedFields.length >= PAIRED_LAYOUT_THRESHOLD;
  const expectedFieldRows = userSchema.length || FALLBACK_SKELETON_ROWS;
  // Lo skeleton deve riservare lo spazio della lista vera: se i campi attesi
  // sono tanti da andare a coppie, anche i rettangoli vanno a coppie —
  // altrimenti riserva una colonna sola e ricrea il salto che deve evitare.
  const skeletonInPairs = expectedFieldRows >= PAIRED_LAYOUT_THRESHOLD;

  return (
    <Box maxWidth={"100%"} sx={{ overflowY: "visible" }}>
      {processId && (
        <Stack spacing={2}>
          {(saveError || Boolean(runtime.error)) && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography color="error" role="alert">{getTranslation(translations, "generic.error.caption") || "Errore nel salvataggio dei campi"}</Typography>
              {Boolean(runtime.error) && (
                <FlowentiButton size="small" onClick={() => { void runtime.reload(); }}>
                  {getTranslation(translations, "generic.retry.caption") || "Riprova"}
                </FlowentiButton>
              )}
            </Stack>
          )}
          {/* Finche' il contesto autorizzato non e' arrivato non si disegna
              nessuna riga: l'altezza va a zero e i campi spingono in basso
              tutto quello che sta sotto quando compaiono. Si riserva invece lo
              spazio dei campi **attesi**, contati dallo userschema del task.

              La guardia e' `!contextMatches` e non `runtime.loading`: il
              secondo e' vero anche mentre si salva, e li' i campi sono gia' a
              schermo — nasconderli per uno skeleton sarebbe peggio del salto
              che stiamo togliendo. */}
          {processId && !contextMatches && !runtime.error && (
            <Grid container maxWidth="100%" spacing={2} role="status" aria-busy="true" data-testid="cf-fields-skeleton">
              {Array.from({ length: expectedFieldRows }).map((_, riga) => (
                <Grid key={`cf-fields-skeleton-${riga}`} item xs={12} sm={skeletonInPairs ? 6 : 12}>
                  <Skeleton variant="rounded" height={FIELD_ROW_HEIGHT} width="100%" />
                </Grid>
              ))}
            </Grid>
          )}
          {contextMatches && (
            <Grid container maxWidth="100%" spacing={2}>
              {renderedFields.map((field) => {
                const extra = entryForRuntimeField(field);
                const [extraKey, extraData] = extra ?? [];
                const isAdHoc = field.adHoc === true;
                return (
                  // L'allegato prende tutta la riga anche nel layout a due
                  // colonne: mezzo spazio tagliava il nome del file.
                  <Grid
                    key={field.fieldId}
                    item
                    xs={12}
                    sm={fieldsInPairs && field.definition.type !== "attachment" ? 6 : 12}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box maxWidth="100%" flex={1} minWidth={0}>
                        <CustomFieldInput
                          field={field}
                          value={runtime.draft ? runtime.draft.values[field.fieldId] : field.value}
                          onChange={(next) => runtime.setValue(field.fieldId, next)}
                          disabled={readonly || !canManage}
                          attachments={trackedAttachments}
                        />
                      </Box>
                      {isAdHoc && extraKey && extraData && canManage && (
                        <Tooltip title={getTranslation(translations, "generic.delete.caption")}>
                          <IconButton
                            onClick={() => void onDelete(extraKey, field.fieldId)}
                            disabled={readonly || loading}
                            data-testid={`userdata-delete-${field.fieldId}`}
                            sx={{ color: "rgba(84, 189, 193, 1)", borderRadius: "6px" }}
                          >
                            <DeleteForeverRounded />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={getTranslation(translations, "generic.showingincard.caption")}>
                        {/* Icona invece di una Checkbox, come nella master dei campi:
                            "in card" e' un interruttore, non un flag da spuntare; lo
                            stato si legge dall'icona piena/vuota
                            (ViewAgenda/ViewAgendaOutlined). */}
                        <IconButton
                          size="small"
                          disabled={loading || savingPreference || !cardPreferences}
                          aria-pressed={Boolean(cardPreferences?.[field.fieldId] ?? field.definition.inCard)}
                          aria-label={`${getTranslation(translations, "generic.showingincard.caption")} ${field.definition.label}`}
                          sx={{
                            color: (cardPreferences?.[field.fieldId] ?? field.definition.inCard)
                              ? "primary.main"
                              : "text.disabled",
                          }}
                          onClick={() => {
                            const current = cardPreferences?.[field.fieldId] ?? field.definition.inCard;
                            // `inCard` del modello è opzionale nel contratto: `Boolean`
                            // lo riporta allo stesso default che mostra l'icona.
                            void onChangeInCardPreference(field.fieldId, !current, Boolean(field.definition.inCard));
                          }}
                          data-testid={`cf-incard-runtime-${field.fieldId}`}
                          id={`button-${field.fieldId}`}
                        >
                          {(cardPreferences?.[field.fieldId] ?? field.definition.inCard) ? (
                            <ViewAgenda fontSize="small" />
                          ) : (
                            <ViewAgendaOutlined fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      )}
      <Stack direction="column" maxWidth={"100%"} spacing={2}>
        <Grid container maxWidth={"100%"} spacing={2}>
          {!processId && userSchema.map(({ id, name, type }) => (
            <Grid
              key={`field-${id}`}
              item
              container
              maxWidth={"100%"}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack flex={1} maxWidth={"100%"}>
                <Stack flex={1} maxWidth={"100%"} direction="row" alignItems="center">
                  {(valori[name]?.value !== undefined || valori[name]?.value !== "" ||
                    enableUndefined) && (
                      <React.Fragment>
                        <Box maxWidth="100%" flex={1} minWidth={0} >
                          <Userfield
                            linkChange={linkChange}
                            value={valori[name]?.value}
                            onChange={(newVal) =>
                              onChangeSchemaValues(name, newVal)
                            }
                            name={name}
                            type={type}
                            readonly={readonly}
                            // Con la definizione si usa il renderer dei tipi:
                            // senza, il campo ricadrebbe sui quattro legacy.
                            definition={definitionByKey.get(name) ?? null}
                            fieldId={id}
                            attachments={attachments}
                          />
                        </Box>
                        <Tooltip
                          title={getTranslation(
                            translations,
                            "start_process.userschema.cannot_delete"
                          )}
                        >
                          <span>
                            <IconButton disabled>
                              <DeleteForeverRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={getTranslation(
                            translations,
                            "generic.showingincard.caption"
                          )}
                          placement="left"
                        >
                          <Checkbox
                            edge="end"
                            disabled={readonly || loading}
                            // || !valori[name]?.value
                            checked={Boolean(valori[name]?.inCard)}
                            onChange={(_e, newChecked) =>
                              onChangeInCardSchema(newChecked, name)
                            }
                            id={name}
                          />
                        </Tooltip>
                      </React.Fragment>)}
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>
        {/* Gli userdata del task che il contesto **non** porta: quelli che non
            sono nello schema e non sono già resi sopra. Aspettano le stesse
            condizioni dei campi veri, perché finché il contesto non è arrivato
            `matchedContext` è null e il filtro che li separa dai campi del
            contesto non esclude niente: comparivano tutti subito, sotto lo
            skeleton e con gli stessi testid dei campi veri. */}
        <Stack spacing={1} pr={2}>
          {contextMatches && Object.entries(value)
            .filter(([k, v]) => !userSchema.map((_) => _.name).includes(k) &&
              !matchedContext?.fields.some((field) => field.fieldId === v.fieldId ||
                (field.definition.legacyKey ?? field.definition.label) === k))
            .map(([key, _value]) => {
              const stagedFiles = _value.fieldId ? pendingFiles?.[_value.fieldId] ?? [] : [];
              return (
                <Stack key={`fields-list-item-${key}`} spacing={0.5}>
                  <FieldsListItem
                    canManage={canManage}
                    onChangeInCard={onChangeInCard}
                    check={_value.inCard}
                    selectedProcess={selectedProcess}
                    item={{ key, value: _value }}
                    onDelete={() => void onDelete(key, _value.fieldId)}
                    onEdit={(key, value) => onChangeSchemaValues(key, value)}
                    //corretto il readonly
                    readonly={readonly}
                    // Gli userdata legacy fuori schema non si creano più: si
                    // mostrano, senza modifiche né cancellazione.
                    valueReadOnly
                    // Anche il ghost usa lo stesso renderer: due liste che rendono
                    // lo stesso campo in due modi diversi sono due bug che si
                    // nascondono a vicenda.
                    definition={definitionByKey.get(key) ?? null}
                    attachments={attachments}
                  />
                  {stagedFiles.map((file) => (
                    <Stack key={file.name} direction="row" alignItems="center" spacing={1} pl={1}>
                      <Typography variant="body2">{file.name}</Typography>
                      <Tooltip title={getTranslation(translations, "generic.delete.caption")}>
                        <IconButton
                          size="small"
                          onClick={() => _value.fieldId && onRemoveFile?.(_value.fieldId, file.name)}
                          disabled={readonly || loading}
                        >
                          <DeleteForeverRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              );
            })}
        </Stack>
      </Stack>
      <Dialog open={open} onClose={() => handleClose()}>
        <form>
          <DialogHeader onClose={() => handleClose()}>
            {getTranslation(translations, "generic.user_data.caption")}
          </DialogHeader>
          <DialogContent>
            {saveError && <Typography color="error" role="alert">{getTranslation(translations, "generic.error.caption")}</Typography>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  id="key-field"
                  size="small"
                  label={getTranslation(
                    translations,
                    "pm.mayactivities.key.label"
                  )}
                  fullWidth
                  error={Boolean(touched.key && errors.key)}
                  helperText={touched.key && errors.key}
                  value={values.key}
                  onChange={({ target }) => changeHandler("key", target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {values.type === "attachment" && onStageFiles ? (
                  <Stack spacing={1}>
                    <Button component="label" size="small" variant="outlined">
                      {getTranslation(translations, "custom_fields.attachment.add")}
                      <input type="file" hidden multiple data-testid="userdata-attachment-input" onChange={(event) => {
                        onStageFiles(newDefinition.fieldId, event.target.files);
                        changeHandler("value", []);
                        event.target.value = "";
                      }} />
                    </Button>
                    {(pendingFiles?.[newDefinition.fieldId] ?? []).map((file) => <Typography key={file.name} variant="body2">{file.name}</Typography>)}
                  </Stack>
                ) : <Userfield
                  name={values.key}
                  linkChange
                  onChange={(v) => changeHandler("value", v)}
                  type={values.type}
                  value={values.value}
                  definition={draftDefinition}
                  attachments={attachments}
                />}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  id="type-field"
                  size="small"
                  label={getTranslation(translations, "placeholder.type.label")}
                  fullWidth
                  error={Boolean(touched.type && errors.type)}
                  helperText={touched.type && errors.type}
                  value={values.type}
                  onChange={({ target }) => {
                    const type = target.value as FieldType;
                    onDiscardFiles?.(newDefinition.fieldId);
                    setNewDefinition(newField(type, 0));
                    changeHandler("type", type);
                    changeHandler("value", type === "boolean" ? false : null);
                  }}
                >
                  {FIELD_GROUPS.flatMap((group) => [
                    <ListSubheader key={group.key}>{getTranslation(translations, groupLabelKey(group.key))}</ListSubheader>,
                    ...group.types.map((type) => (
                      <MenuItem key={type} value={type} data-testid={`userdata-type-${type}`}>
                        {/* L'icona del tipo, la stessa della master dialog dello
                            userschema: MUI ridisegna questo contenuto anche a
                            tendina chiusa, quindi l'icona compare in entrambi i
                            casi e le due tendine si presentano allo stesso modo. */}
                        <TypeIcon type={type} />
                        {getTranslation(translations, typeLabelKey(type))}
                      </MenuItem>
                    )),
                  ])}
                </TextField>
              </Grid>
              {controlsFor(newDefinition.type).resolved.length > 0 && (
                <Grid item xs={12}>
                  <Button startIcon={<Settings />} onClick={() => setConfiguring(true)} data-testid="userdata-configure">
                    {getTranslation(translations, "custom_fields.action.configure")}
                  </Button>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <FlowentiButton size="small" variant="outlined" onClick={() => handleClose()}>
              {getTranslation(
                translations,
                "pm.startprocess.button.cancel.caption"
              )}
            </FlowentiButton>
            <FlowentiButton
              id="confirm-field-button"
              variant="contained"
              size="small"
              type="submit"
              disabled={!isValid || configProblems.length > 0 || loading}
              onClick={handleSubmit}
            >
              {getTranslation(
                translations,
                "pm.mayactivities.confirmation.label"
              )}
            </FlowentiButton>
          </DialogActions>
        </form>
      </Dialog>
      <FieldConfigurationDialog
        open={configuring}
        onClose={() => setConfiguring(false)}
        field={draftDefinition}
        modelId={modelId ?? matchedContext?.modelId}
        additionalFields={formulaDefinitions}
        fields={formulaDefinitions}
        onSave={(field) => {
          setNewDefinition(field);
          setConfiguring(false);
        }}
      />
    </Box>
  );
});

export default FieldsList;
